import { ImagingReportRowData } from "../../tables/notes/imaging-reports-rows";
import { FeaturedCategory } from "../categories";
import { TableRow } from "../../utils/tables-metadata";
import { Smart } from "@metriport/fhir-sdk/dist/types/smart-resources";
import { DiagnosticReport } from "@medplum/fhirtypes";

/**
 * LOINC codes for Echocardiogram imaging reports
 */
const ECHO_LOINC_CODES = [
  "59282-4", // Cardiac stress echo study
  "34552-0", // Cardiac 2D echo panel
  "18010-9", // Aorta Diameter by US
  "85475-2", // US Heart Transesophageal
  "18106-5", // Cardiac echo study Procedure
];

/**
 * LOINC codes for PFT (Pulmonary Function Test) imaging reports
 */
const PFT_LOINC_CODES = [
  "52485-0", // Pulmonary Function Tests
  "81459-0", // Spirometry panel
  "20080-8", // Pulmonary function test method
  "81458-2", // Pulmonary function test panel
];

/**
 * LOINC codes for CT (Computed Tomography) imaging reports
 */
const CT_LOINC_CODES = [
  "46305-9", // CT Whole body
  "24725-4", // CT Head
  "24627-2", // CT Chest
  "24727-0", // CT Head W contrast IV
  "41806-1", // CT Abdomen
  "29252-4", // CT Chest WO contrast
  "30799-1", // CT Head WO contrast
];

/**
 * LOINC codes for MRI (Magnetic Resonance Imaging) reports
 */
const MRI_LOINC_CODES = [
  "30657-1", // MR Brain WO contrast
  "30794-2", // MR Breast
  "36046-1", // MR Liver
  "24589-4", // MR Brain W contrast IV
  "24557-1", // MR Abdomen WO and W contrast IV
  "24587-8", // MR Brain WO and W contrast IV
  "24556-3", // MR Abdomen
];

/**
 * LOINC codes for XR (X-Ray) imaging reports
 */
const XR_LOINC_CODES = [
  "24722-1", // XR Hand 3 Views
  "24799-9", // XR Abdomen AP
  "36643-5", // XR Chest 2 Views
  "22030-1", // Xray/Scan.total Narrative
  "24664-5", // XR Clavicle Views
  "37616-0", // XR Pelvis Single view
];

/**
 * LOINC codes for US (Ultrasound) imaging reports
 *
 * None set for now.
 */
const US_LOINC_CODES: string[] = [];

/**
 * Check if a report is an Echocardiogram imaging report
 */
export function isEchoImagingReport(report: Smart<DiagnosticReport>): boolean {
  const codings = report.code?.coding ?? [];
  function includesEchoInName(s: string): boolean {
    const lower = s.toLowerCase();
    return lower.includes("echo") || lower.includes("echocardiogram");
  }
  const hasEchoInName = codings.some(
    coding => coding.display && includesEchoInName(coding.display)
  );
  const isEchoLoincCode = codings.some(
    coding => coding.code && ECHO_LOINC_CODES.includes(coding.code)
  );
  return hasEchoInName || isEchoLoincCode;
}

/**
 * Check if a report is a PFT (Pulmonary Function Test) imaging report
 */
export function isPFTImagingReport(report: Smart<DiagnosticReport>): boolean {
  const codings = report.code?.coding ?? [];
  function includesPftInName(s: string): boolean {
    const lower = s.toLowerCase();
    return lower.includes("pulmonary") || lower.includes("spirometry") || lower.includes("pft");
  }
  const hasPftInName = codings.some(coding => coding.display && includesPftInName(coding.display));
  const isPftLoincCode = codings.some(
    coding => coding.code && PFT_LOINC_CODES.includes(coding.code)
  );
  return hasPftInName || isPftLoincCode;
}

/**
 * Check if a report is a CT (Computed Tomography) imaging report
 */
export function isCTImagingReport(report: Smart<DiagnosticReport>): boolean {
  const codings = report.code?.coding ?? [];
  function includesCtInName(s: string): boolean {
    return s.toLowerCase().startsWith("ct ") || s.toLowerCase().includes(" ct ");
  }
  const hasCtInName = codings.some(coding => coding.display && includesCtInName(coding.display));
  const isCtLoincCode = codings.some(coding => coding.code && CT_LOINC_CODES.includes(coding.code));
  return hasCtInName || isCtLoincCode;
}

/**
 * Check if a report is an MRI (Magnetic Resonance Imaging) report
 */
export function isMRIImagingReport(report: Smart<DiagnosticReport>): boolean {
  const codings = report.code?.coding ?? [];
  function includesMriInName(s: string): boolean {
    return (
      s.toLowerCase().startsWith("mr ") ||
      s.toLowerCase().includes(" mr ") ||
      s.toLowerCase().includes("mri")
    );
  }
  const hasMriInName = codings.some(coding => coding.display && includesMriInName(coding.display));
  const isMriLoincCode = codings.some(
    coding => coding.code && MRI_LOINC_CODES.includes(coding.code)
  );
  return hasMriInName || isMriLoincCode;
}

/**
 * Check if a report is an XR (X-Ray) imaging report
 */
export function isXRImagingReport(report: Smart<DiagnosticReport>): boolean {
  const codings = report.code?.coding ?? [];
  function includesXrInName(s: string): boolean {
    return s.toLowerCase().startsWith("xr ") || s.toLowerCase().includes(" xr ");
  }
  const hasXrInName = codings.some(coding => coding.display && includesXrInName(coding.display));
  const isXrLoincCode = codings.some(coding => coding.code && XR_LOINC_CODES.includes(coding.code));
  return hasXrInName || isXrLoincCode;
}

/**
 * Check if a report is an Ultrasound imaging report
 */
export function isUltrasoundImagingReport(report: Smart<DiagnosticReport>): boolean {
  const codings = report.code?.coding ?? [];
  function includesUsInName(s: string): boolean {
    return (
      s.toLowerCase().startsWith("us ") ||
      s.toLowerCase().includes(" us ") ||
      s.toLowerCase().startsWith("us-") ||
      s.toLowerCase().includes("ultrasound")
    );
  }
  const hasUsInName = codings.some(coding => coding.display && includesUsInName(coding.display));
  const isUsLoincCode = codings.some(coding => coding.code && US_LOINC_CODES.includes(coding.code));
  return hasUsInName || isUsLoincCode;
}

/**
 * Check if a report is an imaging report. This is a fallback for when the report is not categorized by a LOINC code.
 */
export function isImagingReportBasedOnDisplayValueParsing(
  report: Smart<DiagnosticReport>
): boolean {
  return (
    isEchoImagingReport(report) ||
    isPFTImagingReport(report) ||
    isCTImagingReport(report) ||
    isMRIImagingReport(report) ||
    isXRImagingReport(report) ||
    isUltrasoundImagingReport(report)
  );
}

/**
 * NOTE: All of the display value searches are HACKS.
 * We should have the key metadata we want broken out from the display value upstream and included in the structured data.
 * @returns featured imaging configurations for the featured section
 */
export function getFeaturedImaging(): FeaturedCategory {
  return {
    category: "Imaging reports",
    subCategories: [
      {
        subCategory: "Echocardiogram",
        tableType: "ImagingReports",
        getRows: (data: TableRow[]) =>
          (data as ImagingReportRowData[]).filter(
            r => r.originalData && isEchoImagingReport(r.originalData)
          ),
      },
      {
        subCategory: "PFT (Pulmonary Function Test)",
        tableType: "ImagingReports",
        getRows: (data: TableRow[]) =>
          (data as ImagingReportRowData[]).filter(
            r => r.originalData && isPFTImagingReport(r.originalData)
          ),
      },
      {
        subCategory: "CT (Computed Tomography)",
        tableType: "ImagingReports",
        getRows: (data: TableRow[]) =>
          (data as ImagingReportRowData[]).filter(
            r => r.originalData && isCTImagingReport(r.originalData)
          ),
      },
      {
        subCategory: "MRI (Magnetic Resonance Imaging)",
        tableType: "ImagingReports",
        getRows: (data: TableRow[]) =>
          (data as ImagingReportRowData[]).filter(
            r => r.originalData && isMRIImagingReport(r.originalData)
          ),
      },
      {
        subCategory: "XR (X-Ray)",
        tableType: "ImagingReports",
        getRows: (data: TableRow[]) =>
          (data as ImagingReportRowData[]).filter(
            r => r.originalData && isXRImagingReport(r.originalData)
          ),
      },
    ],
  };
}
